<template>
  <v-col cols="12">
    <gg-card>
      <gg-card-header>
        <gg-card-title>
          Domain Transfer
          <div class="subtitle-2 font-light" style="color: gray">
            Transfer your domain to GreenGeeks in a few <span class="font-italic">simple steps</span>.
          </div>
        </gg-card-title>
      </gg-card-header>
      <gg-card-text>
        <div class="pt-4 pl-4 pr-4">
          <p class="body-1">
            Alright, let's prepare your domain transfer!
          </p>
          <p class="body-1">
            By submitting your request, you acknowledge you have read and understood our
            <a href="https://www.greengeeks.com/support/article/domain-transfer-instructions-primary-domain/"
               target="_blank">Domain Transfer Instructions</a>.
          </p>
        </div>
        <v-form @submit.prevent>
          <v-container class="grid-size-md" fluid>
            <v-row>
              <v-col cols="12" sm="6" class="pb-0">
                <gg-text-field
                  ref="domainInput"
                  label="Domain Name"
                  outlined
                  v-model="model.domain"
                  v-validate="rulesStep1.domain"
                  data-vv-name="domain"
                  data-vv-as="Domain name"
                  :error-messages="errors.collect('domain')"
                  @input="onDomainChange"
                  :disabled="disabledForm"
                  v-on:keyup.enter="checkPricing"
                ></gg-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
        <v-container class="grid-size-md" fluid>
          <v-row class="px-0 pb-5">
            <v-col cols="12" class="pt-0">
              <gg-btn :loading="checkingPrice" :disabled="!canCheckPrice" @click="checkPricing">Check Pricing</gg-btn>
            </v-col>
          </v-row>
        </v-container>
        <gg-card v-if="showAdditionalInformation" class="mb-4">
          <gg-card-header>
            <gg-card-title>
              Additional Information
            </gg-card-title>
          </gg-card-header>
          <gg-card-text>
            <v-form @submit.prevent>
              <v-container class="grid-size-md" fluid>
                <v-row>
                  <v-col v-if="domainTransferResponse.tld_require_eppcode" cols="12" sm="6" class="pb-0">
                    <gg-text-field
                      label="EPP transfer code"
                      hint="Authorization code"
                      outlined
                      v-model="model.epp_code"
                      v-validate="eppValidationRules"
                      data-vv-name="epp"
                      data-vv-as="EPP transfer code"
                      :error-messages="errors.collect('epp')"
                      v-on:keydown.enter="submitRequest"
                    ></gg-text-field>
                  </v-col>
                  <v-col v-if="domainTransferResponse.tld_idprotect_available" cols="12" sm="6">
                    <v-radio-group label="Include Whois Privacy"
                                   hint="See FAQ below for details"
                                   persistent-hint v-model="model.include_whois_privacy">
                      <v-radio label="No" :value="false"></v-radio>
                      <v-radio label="Yes" :value="true"></v-radio>
                    </v-radio-group>
                  </v-col>
                </v-row>
                <div v-if="extendedAttributes.length">
                  <v-row>
                    <v-col class="pt-5 pb-0">
                        <h4>Extended Attributes</h4>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col class="pb-0">
                      <p v-if="domainTransferResponse.tld === 'ca'">
                        You must read and agree to the <a href='https://www.cira.ca/uploads/2023/06/Registrant-Agreement-Version-2.2_0.pdf' target="_newwin">CIRA Registrant Agreement</a> to register a .CA domain.
                      </p>

                      <p v-if="domainTransferResponse.tld === 'eu'">
                        You must read and agree to the <a href='https://cdn.eurid.eu/assets/registration_data_verification_policy-2.pdf' target="_newwin">EURID Registration Data Verification Policy</a> to register a .EU domain.
                      </p>
                    </v-col>
                  </v-row>
                  <v-row
                    v-for="extendedAttribute in extendedAttributes"
                    :key="extendedAttribute.model.name"
                  >
                    <v-col cols="12" sm="6">
                      <gg-input-label class="d-block pb-1">
                        <span v-html="extendedAttribute.description"></span>
                      </gg-input-label>
                      <gg-autocomplete
                        v-if="extendedAttribute.type === 'select'"
                        searchable
                        dense
                        outlined
                        :filter="filter"
                        v-model="extendedAttribute.model.value"
                        :items="extendedAttribute.options"
                        item-value="Value"
                        v-validate="extAttributesRules[`${extendedAttribute.model.name}`]"
                        :data-vv-name="`${extendedAttribute.model.name}`"
                        :error-messages="errors.collect(`${extendedAttribute.model.name}`)"
                      >
                        <template slot="selection" slot-scope="{ item }">
                          {{ item.Title }}
                        </template>
                        <template slot="item" slot-scope="{ item }">
                          <v-row>
                            <v-col cols="11">
                              {{ item.Title }}
                            </v-col>
                            <v-col v-if="typeof item.Description === 'string' && item.Description !== item.Title" cols="1">
                              <v-tooltip bottom max-width="600">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon
                                    color="primary"
                                    dark
                                    v-bind="attrs"
                                    v-on="on"
                                  >
                                    mdi-alert-outline
                                  </v-icon>
                                </template>
                                <span v-html="item.Description"></span>
                              </v-tooltip>
                            </v-col>
                          </v-row>
                        </template>
                      </gg-autocomplete>
                      <gg-text-field
                        v-else-if="extendedAttribute.type === 'input'"
                        outlined
                        v-model="extendedAttribute.model.value"
                        v-validate="extAttributesRules[`${extendedAttribute.model.name}`]"
                        :data-vv-name="`${extendedAttribute.model.name}`"
                        :error-messages="errors.collect(`${extendedAttribute.model.name}`)"
                      ></gg-text-field>
                    </v-col>
                  </v-row>
                </div>
              </v-container>
            </v-form>
          </gg-card-text>
        </gg-card>
        <gg-card v-if="hideSummary" class="mb-4">
          <gg-card-header>
            <gg-card-title>
              Summary
            </gg-card-title>
          </gg-card-header>
          <gg-card-text>
            <v-container>
              <v-row>
                <v-col cols="2" class="font-bold">
                  Transfer Cost:
                </v-col>
                <v-col cols="10">
                  <span v-html="priceWithFreeDomain(formatPrice(domainTransferPrice))"></span>
                </v-col>
              </v-row>
              <template v-if="showDetailedPrice">
                <v-row>
                  <v-col cols="2" class="font-bold">
                    Privacy Cost:
                  </v-col>
                  <v-col cols="10">
                    {{ formatPrice(idProtectPrice) }}
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="2" class="font-bold">
                    Total:
                  </v-col>
                  <v-col cols="10">
                    {{ formatPrice(domainTransferPriceTotal) }}
                  </v-col>
                </v-row>
              </template>
              <v-row>
                <v-col cols="2" class="font-bold">
                  Created On:
                </v-col>
                <v-col cols="10">
                  {{ nowDate() }}
                </v-col>
              </v-row>

            <v-row v-if="!domainTransferResponse.tld_auto_transfer" class="px-1 pb-1">
              <v-col cols="12" class="pt-0">
                <v-col cols="12" class="px-0">
                  <v-checkbox
                    v-model="model.requestConfirmed"
                    color="primary"
                    v-validate="confirmationValidationRules"
                    data-vv-name="confirmationRule"
                    :error-messages="errors.collect('confirmationRule')"
                    aria-label="I confirm the details of my domain transfer request are correct. I agree to the Domain Transfer Instructions and ICANN policies."
                    class="mt-0"
                    :ripple="true"
                  >
                    <template v-slot:label>
                      <label class="v-label" style="cursor:pointer;" aria-hidden="true">I confirm the details of my domain transfer request are correct. I agree to the Domain Transfer Instructions and ICANN policies.</label>
                    </template>
                  </v-checkbox>
                </v-col>
                <gg-btn :loading="submittingRequest" :disabled="canCheckPrice" @click="submitRequest" class="mr-4">Submit Request</gg-btn>
              </v-col>
            </v-row>
            </v-container>
          </gg-card-text>
        </gg-card>
        <gg-card v-if="domainTransferResponse.tld_auto_transfer" class="mb-4">
          <gg-card-header>
            <gg-card-title>
              Payment
            </gg-card-title>
          </gg-card-header>
          <gg-card-text>
            <v-container class="grid-size-md" fluid>
              <payment-gateway
                compact
                ref="paymentGateway"
                @gateway="listenGateway"
                @card="listenCard"
                @card-switch="listenCardNew"
                with-redeem
                :card-selection-label="cardLabel"
                :with-free-domain-migration="isWithFreeDomainMigration"
                :free-domain-options="freeDomainOptions"
                :price-with-free-domain="model.include_whois_privacy ? domainTransferPriceTotal : 0"
              >
                <template v-slot:header>Domain Transfer Payment</template>
              </payment-gateway>
              <v-row class="px-0 pb-1">
                <v-col cols="12" class="pt-0">
                  <v-col cols="12" class="px-0">
                    <v-checkbox
                      v-model="model.requestConfirmed"
                      color="primary"
                      v-validate="confirmationValidationRules"
                      data-vv-name="confirmationRule"
                      :error-messages="errors.collect('confirmationRule')"
                      aria-label="I confirm the details of my domain transfer request are correct. I agree to the Domain Transfer Instructions and ICANN policies."
                      class="mt-0"
                      :ripple="true"
                    >
                      <template v-slot:label>
                        <label class="v-label" style="cursor:pointer;" aria-hidden="true">I confirm the details of my domain transfer request are correct. I agree to the Domain Transfer Instructions and ICANN policies.</label>
                      </template>
                    </v-checkbox>
                  </v-col>
                  <gg-btn :loading="submittingRequest" :disabled="canCheckPrice" @click="submitTransfer" class="mr-4">Submit Transfer</gg-btn>
                </v-col>
              </v-row>
            </v-container>
          </gg-card-text>
        </gg-card>
      </gg-card-text>
    </gg-card>
  </v-col>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import PaymentGateway from '@/components/Common/PaymentGatewayWithFreeDomainMigration'
import GGAutocomplete from '../Global/Inputs/GGAutocomplete.vue'

export default {
  name: 'DomainTransfer',

  components: {
    PaymentGateway,
    GgAutocomplete: GGAutocomplete,
  },

  data: () => ({
    model: {
      name: '',
      email: '',
      domain: '',
      epp_code: '',
      include_whois_privacy: false,
      requestConfirmed: false,
    },
    checkingPrice: false,
    submittingRequest: false,
    rulesStep1: {
      domain: {
        required: true,
        url: true,
      },
    },
    domainTransferResponse: {},
    extendedAttributes: [],
    extendedAttributesRules: {},
    isSubmitting: false,
    gateway: '',
    isCardNew: false,
    freeDomainOptions: [],
    freezeDomain: '',
  }),
  methods: {
    ...mapActions([ 'domainTransferRequest', ]),
    fillData () {
      this.model.name = `${this.SessionProfile.firstname} ${this.SessionProfile.lastname}`
      this.model.email = this.SessionProfile.email
    },
    async checkPricing () {
      if (this.freezeDomain === this.model.domain) {
        return
      }
      this.freezeDomain = this.model.domain
      try {
        this.domainTransferResponse = {}
        await this.$validator.reset()
        await this.$nextTick()
        const valid = await this.$validator.validateAll()
        if (!valid) {
          return
        }
        this.canValidateOnFly = false
        this.checkingPrice = true
        const data = {
          action: 'step1',
          domain: this.model.domain,
        }
        this.domainTransferResponse = await this.domainTransferRequest(data)
        this.resetExtendedAttributes()
        this.buildExtendedAttributes()
        this.setFreeMigrationServices()
        this.resetModel()
        this.model.domain = this.freezeDomain
      } catch (e) {
        this.freezeDomain = ''
      } finally {
        this.checkingPrice = false
      }
    },
    async submitRequest () {
      if (this.domainTransferResponse.tld_auto_transfer) {
        return this.submitTransfer()
      }
      try {
        this.isSubmitting = true
        await this.$nextTick()
        const valid = await this.$validator.validateAll()
        if (!valid) {
          return
        }
        this.submittingRequest = true
        const data = {
          action: 'step2',
          domain: this.model.domain,
          epp_code: this.model.epp_code,
          request_confirmed: +this.model.requestConfirmed,
          whois_privacy: this.model.include_whois_privacy ? 1 : 0
        }
        this.extendedAttributes.forEach((extendedAttribute) => {
          data[extendedAttribute.model.name] = extendedAttribute.model.value
        })
        const response = await this.domainTransferRequest(data)
        this.handleDomainTransferSubmitResponse(response)
      } catch (e) {
      } finally {
        this.isSubmitting = true
        this.submittingRequest = false
      }
    },
    async submitTransfer () {
      try {
        await this.$validator.reset()
        this.isSubmitting = true
        await this.$nextTick()
        const valid = await this.$validator.validateAll()
        const validPay = await this.$refs.paymentGateway.validateForm()
        if (!valid || !validPay) {
          return
        }
        this.submittingRequest = true
        const data = {
          action: 'step2',
          domain: this.model.domain,
          epp_code: this.model.epp_code,
          request_confirmed: +this.model.requestConfirmed,
          whois_privacy: this.model.include_whois_privacy ? 1 : 0,
          cardnum: this.$refs.paymentGateway.isCardNew === true ? this.$refs.paymentGateway.model.cardnum : '',
          expdate: this.$refs.paymentGateway.isCardNew === true ? this.$refs.paymentGateway.model.expdate : '',
          cardnum_cvv: this.$refs.paymentGateway.isCardNew !== 'freeDomain' ? this.$refs.paymentGateway.model.cardnum_cvv : '',
          free_domain_service_id: this.gateway === 'freeDomain' ? this.$refs.paymentGateway.freeDomainService : '',
        }
        this.extendedAttributes.forEach((extendedAttribute) => {
          data[extendedAttribute.model.name] = extendedAttribute.model.value
        })
        const response = await this.domainTransferRequest(data)
        this.handleDomainTransferSubmitResponse(response)
      } catch (e) {
      } finally {
        this.isSubmitting = true
        this.submittingRequest = false
      }
    },
    handleDomainTransferSubmitResponse (response) {
      const { ticket_created: ticketCreated, ticket_id: ticketid, domain_transfer_id: domainTransferId } = response
      const name = ticketCreated ? 'ticket_view' : 'domaintransfer_view'
      const params = ticketCreated ? { ticketid } : { domainTransferId }
      this.$router.push({ name, params, })
    },
    buildExtendedAttributes () {
      if (!this.domainTransferResponse.tld_extended_attributes?.length) {
        return
      }
      this.domainTransferResponse.tld_extended_attributes.forEach((extAttribute) => {
        const attribute = {
          model: { name: extAttribute.Name, value: '' },
          type: extAttribute.UserDefined === 'False' ? 'select' : 'input',
          description: extAttribute.Description,
          options: extAttribute.UserDefined === 'False' && extAttribute.Options.Option.length
            ? Array.from(extAttribute.Options.Option)
            : [extAttribute.Options.Option]
        }
        this.extendedAttributes.push(attribute)
        if (extAttribute.Required === '1') {
          Object.assign(this.extendedAttributesRules, { [extAttribute.Name]: { required: true } })
        }
      })
    },
    resetExtendedAttributes () {
      this.extendedAttributes = []
      this.extendedAttributesRules = {}
    },
    formatPrice (price) {
      if (typeof price === 'number') {
        return `$${price.toFixed(2)} USD`
      }
      return 'N/A'
    },
    nowDate () {
      const today = new Date()
      return today.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    },
    listenGateway (gateway) {
      this.gateway = gateway
    },
    listenCard (card) {
      this.model.card = card
    },
    listenCardNew (card) {
      this.isCardNew = card
    },
    setFreeMigrationServices () {
      if (!this.domainTransferResponse?.free_domain_service_ids.length) {
        return
      }
      this.freeDomainOptions = []
      const freeMigrationServices = this.services.filter((service) => {
        return this.domainTransferResponse.free_domain_service_ids.includes(parseInt(service.id))
      })
      freeMigrationServices.forEach((service) => {
        this.freeDomainOptions.push({ label: `${service.groupname} - ${service.domain}`, value: service.id })
      })
    },
    resetModel () {
      this.model = {
        domain: '',
        name: '',
        email: '',
        epp_code: '',
        include_whois_privacy: false,
        requestConfirmed: false,
      }
    },
    resetForm () {
      this.$validator.reset()
      this.model.epp_code = ''
      this.model.include_whois_privacy = false
      this.model.requestConfirmed = false
      this.extendedAttributes = []
      this.extendedAttributesRules = {}
      this.domainTransferResponse = {}
    },
    onDomainChange () {
      this.resetForm()
    },
    filter (item, searchText) {
      return item.Title.toLowerCase().indexOf(searchText.toLocaleLowerCase()) > -1
    },
    priceWithFreeDomain (priceText) {
      if (this.isFreeDomain) {
        return `<span style="text-decoration: line-through;">${priceText}</span> Free Domain Credit`
      }
      return priceText
    },
  },

  computed: {
    ...mapGetters(['SessionProfile', 'services']),
    eppValidationRules () {
      return {
        required: this.isSubmitting && this.domainTransferResponse.tld_require_eppcode
      }
    },
    confirmationValidationRules () {
      return `confirmed:${this.isSubmitting ? 'true' : 'false'}`
    },
    domainTransferPrice () {
      return this.domainTransferResponse.tld_price || ''
    },
    idProtectPrice () {
      return this.domainTransferResponse.tld_idprotect_price || ''
    },
    domainTransferPriceTotal () {
      if (this.isFreeDomain) {
        return this.domainTransferResponse.tld_idprotect_price
      }
      return (this.domainTransferResponse.tld_price + this.domainTransferResponse.tld_idprotect_price) || ''
    },
    extAttributesRules () {
      const rules = {}
      if (!this.isSubmitting) {
        return rules
      }
      Object.assign(rules, this.extendedAttributesRules)
      this.extendedAttributes.forEach((extendedAttribute) => {
        if (!extendedAttribute.options.length) {
          return
        }
        const selectedOption = extendedAttribute.options.find((option) => {
          return option?.Value === extendedAttribute.model.value
        })
        if (!selectedOption || !selectedOption.Requires) {
          return
        }
        Object.assign(rules, { [selectedOption.Requires.Attribute.Name]: { required: true } })
      })
      return rules
    },
    showAdditionalInformation () {
      return this.domainTransferResponse?.tld_extended_attributes?.length ||
        this.domainTransferResponse.tld_require_eppcode ||
        this.domainTransferResponse.tld_idprotect_available
    },
    showDetailedPrice () {
      return this.domainTransferResponse.tld_idprotect_available && this.model.include_whois_privacy
    },
    cardLabel () {
      if (this.domainTransferResponse.tld_auto_transfer && this.$refs.paymentGateway) {
        return typeof this.$refs.paymentGateway.isCardNew === 'boolean' ? '' : this.$refs.paymentGateway.isCardNew
      }
      return ''
    },
    isWithFreeDomainMigration () {
      return !!this.domainTransferResponse?.free_domain_service_ids.length
    },
    disabledForm () {
      return this.checkingPrice || this.submittingRequest
    },
    hideSummary () {
      return Object.keys(this.domainTransferResponse).length
    },
    canProceed () {
      return this.freezeDomain === this.model.domain
    },
    canCheckPrice () {
      return this.freezeDomain !== this.model.domain
    },
    isFreeDomain () {
      return this.gateway === 'freeDomain'
    },
  },

  created () {
    this.fillData()
  },
}
</script>
